import { Flex } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { GetCommitmentsForLimitedPartnerQueryGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { SkeletonCards, SummaryCard } from './shared';

const calculateSummaryStats = (
  commitmentData: GetCommitmentsForLimitedPartnerQueryGQLType,
) => {
  let committedCapital = 0;
  let uncalledCapital = 0;
  const creditFacilityIdSet = new Set<string>();
  commitmentData.limitedPartnerCommitments.forEach((commitment) => {
    committedCapital += commitment.committedCapital;
    uncalledCapital += commitment.uncalledCapital;
    creditFacilityIdSet.add(commitment.creditFacilityId);
  });
  return {
    committedCapital,
    uncalledCapital,
    facilityCount: creditFacilityIdSet.size,
    commitmentCount: commitmentData.limitedPartnerCommitments.length,
  };
};

const useSummaryStats = (
  commitmentData: GetCommitmentsForLimitedPartnerQueryGQLType,
) => {
  const {
    committedCapital,
    uncalledCapital,
    commitmentCount,
    facilityCount,
  } = useMemo(() => {
    return calculateSummaryStats(commitmentData);
  }, [commitmentData]);
  return { committedCapital, uncalledCapital, commitmentCount, facilityCount };
};

const DetailSummaryCards = ({
  commitmentData,
}: {
  commitmentData: GetCommitmentsForLimitedPartnerQueryGQLType;
}) => {
  const {
    committedCapital,
    uncalledCapital,
    commitmentCount,
    facilityCount,
  } = useSummaryStats(commitmentData);

  return (
    <Flex gap="1.5rem">
      <SummaryCard
        label="Total Commitment Capital"
        value={
          commonUtils.numberToCurrencyWithSymbol({
            number: Number(committedCapital),
          }) ?? '-'
        }
      />
      <SummaryCard
        label="Total Uncalled Capital"
        value={
          commonUtils.numberToCurrencyWithSymbol({
            number: Number(uncalledCapital),
          }) ?? '-'
        }
      />
      <SummaryCard label="Facilities" value={String(facilityCount)} />
      <SummaryCard label="LP Commitments" value={String(commitmentCount)} />
    </Flex>
  );
};

export const LimitedPartnerDetailSummaryCards = ({
  commitmentData,
  refetchingCommitments,
}: {
  commitmentData: GetCommitmentsForLimitedPartnerQueryGQLType | undefined;
  refetchingCommitments: boolean;
}) => {
  if (!commitmentData || refetchingCommitments) {
    return <SkeletonCards />;
  }
  return <DetailSummaryCards commitmentData={commitmentData} />;
};
